
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import PaymentOptions from './components/PaymentOptions';
import BookingConfirmation from './components/BookingConfirmation';
import { Link } from 'react-router-dom';

interface Workshop {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  in_person_price: number;
  virtual_price: number;
  max_participants: number;
  includes: string[];
  image_url: string;
}

export default function Workshops() {
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const [bookingFormData, setBookingFormData] = useState<any>(null);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    workshopType: '',
    format: '',
    preferredDate: '',
    groupSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    fetchWorkshops();

    // Check for payment success/cancel from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success) {
      alert('Payment successful! Your workshop booking has been confirmed. You will receive an email confirmation shortly.');
      // Clear URL params
      window.history.replaceState(null, document.title, window.location.pathname);
    } else if (canceled) {
      alert('Payment was cancelled. Please try booking again.');
      // Clear URL params
      window.history.replaceState(null, document.title, window.location.pathname);
    }
  }, []);

  const fetchWorkshops = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-workshops`, {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });

      const result = await response.json();
      if (result.workshops) {
        setWorkshops(result.workshops);
      }
    } catch (error) {
      console.error('Error fetching workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWorkshopSelect = (workshop: Workshop) => {
    setSelectedWorkshop(workshop);
    setFormData(prev => ({
      ...prev,
      workshopType: workshop.id
    }));
  };

  const handleFormatSelect = (format: string) => {
    setSelectedFormat(format);
    setFormData(prev => ({
      ...prev,
      format: format
    }));
  };

  const handleBookNow = () => {
    if (selectedWorkshop && selectedFormat) {
      setShowBookingForm(true);
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const totalAmount = selectedFormat === 'in-person' 
        ? selectedWorkshop?.in_person_price 
        : selectedWorkshop?.virtual_price;

      const bookingData = {
        workshopTitle: selectedWorkshop?.title || '',
        format: selectedFormat,
        price: totalAmount || 0,
        date: formData.preferredDate,
        participantName: formData.name,
        participantEmail: formData.email,
        participantPhone: formData.phone,
        organization: formData.organization,
        groupSize: formData.groupSize,
        message: formData.message
      };

      setBookingFormData(bookingData);
      setShowBookingForm(false);
      setShowPaymentOptions(true);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSelect = (method: string) => {
    setShowPaymentOptions(false);
    setShowBookingConfirmation(true);
  };

  const resetBookingFlow = () => {
    setShowBookingForm(false);
    setShowPaymentOptions(false);
    setShowBookingConfirmation(false);
    setSelectedWorkshop(null);
    setSelectedFormat('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      workshopType: '',
      format: '',
      preferredDate: '',
      groupSize: '',
      message: ''
    });
    setSubmitStatus('idle');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
            <p className="text-slate-600">Loading workshops...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://public.readdy.ai/ai/img_res/eee2bbd23b2513e997cc9bb8b2a41dd1.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl font-bold mb-6">The Dream Journal Workshops</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Transform your life through guided journaling experiences. Available both in-person and virtually to help
            you discover your purpose and turn dreams into reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 text-slate-800 hover:bg-yellow-400 font-semibold"
              onClick={() => document.getElementById('workshops-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Book Your Workshop
            </Button>
          </div>
        </div>
      </section>

      {/* Workshops & Speaking */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Workshops</h2>
            <p className="text-xl text-slate-600">Transformational experiences that inspire and empower</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {[
              {
                title: "Dream Again Workshop",
                description: "Rediscover your purpose and reignite the dreams that have been dormant in your heart.",
                icon: "ri-lightbulb-line",
                image: "https://readdy.ai/api/search-image?query=inspiring%20workshop%20setting%20with%20people%20engaged%20in%20dream%20mapping%20exercise%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20deep%20blue%20and%20gold%20accents&width=400&height=250&seq=workshop1&orientation=landscape"
              },
              {
                title: "Vision Mapping Intensive",
                description: "Create a clear roadmap for your future with practical tools and spiritual guidance.",
                icon: "ri-map-line",
                image: "https://readdy.ai/api/search-image?query=vision%20mapping%20session%20with%20participants%20working%20on%20goal%20boards%2C%20organized%20workspace%2C%20planning%20materials%2C%20inspiring%20environment&width=400&height=250&seq=mapping1&orientation=landscape"
              }
            ].map((workshop, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={workshop.image}
                  alt={workshop.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                    <i className={`${workshop.icon} text-2xl text-blue-800`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">{workshop.title}</h3>
                  <p className="text-slate-600 mb-4">{workshop.description}</p>
                  <Link to="/workshops">
                    <Button variant="outline" size="sm" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                      Book Workshop
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Workshop Options */}
          <section id="workshops-section" className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-slate-800 mb-4">Choose Your Workshop Experience</h2>
                <p className="text-xl text-slate-600">Select the format and workshop that best fits your needs</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {workshops.map(workshop => (
                  <div
                    key={workshop.id}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden transition-all ${
                      selectedWorkshop?.id === workshop.id ? 'ring-2 ring-blue-500 shadow-xl' : 'hover:shadow-xl'
                    }`}
                  >
                    <img src={workshop.image_url} alt={workshop.title} className="w-full h-48 object-cover object-top" />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">{workshop.duration}</span>
                        {selectedWorkshop?.id === workshop.id && <i className="ri-check-circle-fill text-2xl text-blue-500"></i>}
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">{workshop.title}</h3>
                      <p className="text-blue-600 font-medium mb-3">{workshop.subtitle}</p>
                      <p className="text-slate-600 mb-4">{workshop.description}</p>

                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2">Includes:</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          {workshop.includes.slice(0, 3).map((item, index) => (
                            <li key={index} className="flex items-center">
                              <i className="ri-check-line text-green-500 mr-2"></i>
                              {item}
                            </li>
                          ))}
                          {workshop.includes.length > 3 && (
                            <li className="text-blue-600">+ {workshop.includes.length - 3} more...</li>
                          )}
                        </ul>
                      </div>

                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="text-sm text-slate-600">In-Person</div>
                          <div className="text-lg font-bold text-slate-800">${workshop.in_person_price}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Virtual</div>
                          <div className="text-lg font-bold text-slate-800">${workshop.virtual_price}</div>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleWorkshopSelect(workshop)}
                        className={`w-full ${
                          selectedWorkshop?.id === workshop.id 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-slate-800 hover:bg-slate-900'
                        }`}
                      >
                        {selectedWorkshop?.id === workshop.id ? 'Selected' : 'Select Workshop'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Format Selection */}
          {selectedWorkshop && (
            <section className="py-16 bg-slate-50">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Choose Your Format</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* In-Person Card */}
                  <div
                    className={`bg-white rounded-lg p-6 cursor-pointer transition-all ${
                      selectedFormat === 'in-person' ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-lg'
                    }`}
                    onClick={() => handleFormatSelect('in-person')}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                        <i className="ri-group-line text-2xl text-blue-600"></i>
                      </div>
                      {selectedFormat === 'in-person' && <i className="ri-check-circle-fill text-2xl text-blue-500"></i>}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">In-Person Workshop</h3>
                    <p className="text-slate-600 mb-4">
                      Join us for an immersive, hands-on experience with direct interaction, networking opportunities,
                      and the full energy of group transformation.
                    </p>
                    <div className="text-2xl font-bold text-blue-600">
                      ${selectedWorkshop?.in_person_price}
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-slate-600">
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Face-to-face interaction with Elbert
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Networking with like-minded individuals
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Physical materials and resources
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Group energy and collaboration
                      </li>
                    </ul>
                  </div>

                  {/* Virtual Card */}
                  <div
                    className={`bg-white rounded-lg p-6 cursor-pointer transition-all ${
                      selectedFormat === 'virtual' ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-lg'
                    }`}
                    onClick={() => handleFormatSelect('virtual')}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
                        <i className="ri-video-line text-2xl text-green-600"></i>
                      </div>
                      {selectedFormat === 'virtual' && <i className="ri-check-circle-fill text-2xl text-blue-500"></i>}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Virtual Workshop</h3>
                    <p className="text-slate-600 mb-4">
                      Experience the same transformational content from the comfort of your home, with interactive elements
                      and personalized attention.
                    </p>
                    <div className="text-2xl font-bold text-green-600">
                      ${selectedWorkshop?.virtual_price}
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-slate-600">
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Join from anywhere in the world
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Interactive online activities
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Digital resources and materials
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        Recording access for 30 days
                      </li>
                    </ul>
                  </div>
                </div>

                {selectedFormat && (
                  <div className="text-center mt-8">
                    <Button
                      onClick={handleBookNow}
                      size="lg"
                      className="bg-blue-800 hover:bg-blue-900 px-12"
                    >
                      Book Now - ${selectedFormat === 'in-person' ? selectedWorkshop?.in_person_price : selectedWorkshop?.virtual_price}
                    </Button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Booking Form Modal */}
          {showBookingForm && selectedWorkshop && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-slate-800">Book Your Workshop</h3>
                  <button 
                    onClick={() => setShowBookingForm(false)}
                    className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                {/* Selected Workshop Summary */}
                <div className="bg-blue-50 rounded-lg p-6 mb-8">
                  <h4 className="font-bold text-slate-800 mb-2">Selected Workshop:</h4>
                  <p className="text-blue-800 font-semibold">{selectedWorkshop.title}</p>
                  <p className="text-slate-600">{selectedFormat === 'in-person' ? 'In-Person' : 'Virtual'} Format</p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">
                    ${selectedFormat === 'in-person' ? selectedWorkshop.in_person_price : selectedWorkshop.virtual_price}
                  </p>
                </div>

                <form onSubmit={handleBookingSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-slate-700 mb-2">
                        Organization (Optional)
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Company or organization name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="preferredDate" className="block text-sm font-medium text-slate-700 mb-2">
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="groupSize" className="block text-sm font-medium text-slate-700 mb-2">
                        Expected Group Size *
                      </label>
                      <select
                        id="groupSize"
                        name="groupSize"
                        value={formData.groupSize}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                      >
                        <option value="">Select group size</option>
                        <option value="1-5">1-5 people</option>
                        <option value="6-15">6-15 people</option>
                        <option value="16-30">16-30 people</option>
                        <option value="31-50">31-50 people</option>
                        <option value="50+">50+ people</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      placeholder="Tell us about your goals, special requirements, or any questions you have (max 500 characters)"
                    />
                    <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <div className="flex space-x-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-800 hover:bg-blue-900"
                      disabled={isSubmitting}
                      size="lg"
                    >
                      {isSubmitting ? 'Processing...' : 'Continue to Payment'}
                    </Button>
                    <Button
                      onClick={() => setShowBookingForm(false)}
                      variant="outline"
                      className="border-slate-400 text-slate-600 hover:bg-slate-100"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Payment Options Modal */}
          {showPaymentOptions && selectedWorkshop && bookingFormData && (
            <PaymentOptions
              workshopData={{
                title: selectedWorkshop.title,
                format: selectedFormat,
                price: selectedFormat === 'in-person' ? selectedWorkshop.in_person_price : selectedWorkshop.virtual_price,
                date: formData.preferredDate
              }}
              bookingData={bookingFormData}
              onPaymentSelect={handlePaymentSelect}
              onClose={() => setShowPaymentOptions(false)}
            />
          )}

          {/* Booking Confirmation Modal */}
          {showBookingConfirmation && bookingFormData && (
            <BookingConfirmation
              bookingData={{
                workshopTitle: bookingFormData.workshopTitle,
                format: bookingFormData.format,
                price: bookingFormData.price,
                date: bookingFormData.date,
                name: bookingFormData.participantName,
                email: bookingFormData.participantEmail
              }}
              onClose={resetBookingFlow}
            />
          )}

          {/* Testimonials */}
          <section className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">What Participants Say</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    name: 'Sarah Johnson',
                    role: 'Marketing Director',
                    text:
                      'The Dream Journal workshop completely transformed how I approach goal-setting. Elbert\'s guidance helped me discover my true purpose and create a clear path forward.',
                    rating: 5
                  },
                  {
                    name: 'Michael Chen',
                    role: 'Entrepreneur',
                    text:
                      'I attended the virtual workshop and was amazed by how engaging and personal it felt. The tools I learned have become part of my daily routine.',
                    rating: 5
                  },
                  {
                    name: 'Lisa Rodriguez',
                    role: 'Team Leader',
                    text:
                      'Our corporate team workshop was incredible. Everyone left with renewed purpose and better alignment with our company mission. Highly recommend!',
                    rating: 5
                  }
                ].map((testimonial, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                    <div className="flex text-yellow-400 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill"></i>
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                    <div>
                      <div className="font-semibold text-slate-800">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {[
                  {
                    question: "What's the difference between in-person and virtual workshops?",
                    answer:
                      'Both formats cover the same transformational content. In-person workshops offer face-to-face interaction and networking, while virtual workshops provide convenience and include 30-day recording access. Both include all materials and resources.'
                  },
                  {
                    question: 'Do I need to read The Dream Journal before attending?',
                    answer:
                      'No prior reading is required! Each workshop includes a copy of The Dream Journal, and we\'ll guide you through the key concepts during the session. However, if you\'ve already read it, you\'ll gain even deeper insights.'
                  },
                  {
                    question: 'Can I book a private workshop for my organization?',
                    answer:
                      'Absolutely! I offer customized workshops for organizations of all sizes. Contact me to discuss your specific needs, and I\'ll create a tailored experience for your team.'
                  },
                  {
                    question: 'What if I need to reschedule or cancel?',
                    answer:
                      'I understand that schedules change. You can reschedule up to 48 hours before the workshop date. Cancellations made 7+ days in advance receive a full refund.'
                  },
                  {
                    question: 'Are the workshops suitable for beginners?',
                    answer:
                      'Yes! The workshops are designed for people at all stages of their personal growth journey. Whether you\'re just starting to explore your purpose or looking to deepen your practice, you\'ll find value.'
                  }
                ].map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
                    <h3 className="font-semibold text-slate-800 mb-2">{faq.question}</h3>
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </section>
    </div>
  );
}
